import type { ThemeColor } from "@/colors";
import { getColor } from "@/colors";
import { $t } from "@/services/i18n";

/**
 * Charge subtypes supported by the system
 * These represent different types of billable operations
 */
export type ChargeSubtype =
  | "text-generation"
  | "speech-to-text"
  | "text-to-speech"
  | "embedding"
  | "anonymization"
  | "audio-enhancement"
  | "text-classification"
  | "instance"
  | "agent"
  | "bot"
  | "insight"
  | "llm_boost"
  | "telephony_did"
  | "decision";

/**
 * Information about a charge subtype including icon, color and translation
 */
export interface ChargeSubtypeInfo {
  icon: string;
  color: ThemeColor;
  translationKey: string;
}

/**
 * Centralized configuration for all charge subtypes
 * All icons use Lucide icons for consistency
 */
const CHARGE_SUBTYPE_CONFIG: Record<ChargeSubtype, Omit<ChargeSubtypeInfo, "translationKey">> = {
  "text-generation": {
    icon: "lucide:sparkles",
    color: "amber",
  },
  "speech-to-text": {
    icon: "lucide:mic",
    color: "violet",
  },
  "text-to-speech": {
    icon: "lucide:volume-2",
    color: "cyan",
  },
  embedding: {
    icon: "lucide:database",
    color: "blue",
  },
  anonymization: {
    icon: "lucide:shield-check",
    color: "red",
  },
  "audio-enhancement": {
    icon: "lucide:music",
    color: "red",
  },
  "text-classification": {
    icon: "lucide:tag",
    color: "purple",
  },
  instance: {
    icon: "lucide:server",
    color: "brown",
  },
  agent: {
    icon: "lucide:bot",
    color: "crimson",
  },
  bot: {
    icon: "lucide:bot",
    color: "orange",
  },
  insight: {
    icon: "lucide:lightbulb",
    color: "jade",
  },
  llm_boost: {
    icon: "lucide:sparkles",
    color: "iris",
  },
  telephony_did: {
    icon: "lucide:phone",
    color: "green",
  },
  decision: {
    icon: "lucide:git-branch",
    color: "teal",
  },
};

/**
 * Get complete information about a charge subtype
 * @param subtype - The charge subtype to look up
 * @returns Configuration object with icon, color, and translation key
 */
export function getChargeSubtypeInfo(subtype: string): ChargeSubtypeInfo {
  const config = CHARGE_SUBTYPE_CONFIG[subtype as ChargeSubtype];

  if (!config) {
    // Fallback for unknown subtypes
    return {
      icon: "lucide:help-circle",
      color: "gray",
      translationKey: `charge_subtypes.unknown`,
    };
  }

  return {
    ...config,
    translationKey: `charge_subtypes.${subtype}`,
  };
}

/**
 * Get translated label for a charge subtype
 * @param subtype - The charge subtype
 * @returns Translated label
 */
export function getChargeSubtypeLabel(subtype: string): string {
  const info = getChargeSubtypeInfo(subtype);
  return $t(info.translationKey as any);
}

/**
 * Get color value for a charge subtype
 * @param subtype - The charge subtype
 * @param options - Color options (opacity, shade)
 * @returns Color value as string
 */
export function getChargeSubtypeColor(
  subtype: string,
  options?: { opacity?: number; shade?: number }
): string {
  const info = getChargeSubtypeInfo(subtype);
  return getColor(info.color, options);
}

/**
 * Get all supported charge subtypes
 * @returns Array of all charge subtype strings
 */
export function getAllChargeSubtypes(): ChargeSubtype[] {
  return Object.keys(CHARGE_SUBTYPE_CONFIG) as ChargeSubtype[];
}

/**
 * Compatibility layer for components using old getModelDesign format
 * Returns information in a format compatible with the old model.store modelTypes
 * @param type - The charge subtype
 * @returns Design object with icon and color
 */
export function getChargeSubtypeDesign(
  type: string
): { icon: string; color: ThemeColor } | undefined {
  const info = getChargeSubtypeInfo(type);

  // Return undefined for unknown types (to match old behavior)
  if (info.translationKey === "charge_subtypes.unknown") {
    return undefined;
  }

  return {
    icon: info.icon,
    color: info.color,
  };
}

/**
 * Get all charge subtypes in the format compatible with old modelTypes array
 * @returns Array of charge subtype objects with name, icon, color, key, and label
 */
export function getChargeSubtypesList() {
  // Map charge subtypes to their legacy keys for backwards compatibility
  const keyMap: Partial<Record<ChargeSubtype, string>> = {
    "text-generation": "llm",
    "speech-to-text": "asr",
    "text-to-speech": "tts",
  };

  return getAllChargeSubtypes().map((type) => {
    const info = getChargeSubtypeInfo(type);
    return {
      name: type,
      icon: info.icon,
      color: info.color,
      key: keyMap[type] ?? type,
      label: $t(info.translationKey as any),
    };
  });
}

/**
 * Legacy fallback function for feature design lookup
 * Returns a default design for unknown feature types
 * @deprecated Features are deprecated - use getChargeSubtypeInfo instead
 */
export function getFeatureDesign(feature: string): {
  icon: string;
  color: ThemeColor;
  key: string;
} {
  // Try to match as a charge subtype first
  const subtypeDesign = getChargeSubtypeDesign(feature);
  if (subtypeDesign) {
    return {
      ...subtypeDesign,
      key: feature,
    };
  }

  // Fallback for unknown types
  return {
    color: "orange",
    icon: "lucide:box",
    key: feature,
  };
}
